import {
    Box,
    Center,
    Flex,
    Spinner,
    Image,
    Skeleton,
    useColorMode,
    Button,
} from '@chakra-ui/react';
import { useContext, useEffect, useState } from 'react';
import { BrowserView } from 'react-device-detect';
import { BrowserSidebar } from '../component/globalComponents/BrowserSidebar';
import { BrowserTopBar } from '../component/globalComponents/BrowserTopBar';
import { PrivateFollowStats } from '../component/profileComponents/PrivateFollowStats';
import config from '../config';
import { usePageDataCache } from '../hooks/usePageDataCache';
import {
    getUserFollowersCount,
    getUserFollowingCount,
    getUserPrivateProfile,
    UserProfile,
} from '../service/social';
import LightHeader from '../assets/headers/profile-light.png';
import DarkHeader from '../assets/headers/profile-dark.png';
import { ProfileTabs } from '../component/profileComponents/ProfileTabs';
import { PrivateProfileInfo } from '../component/profileComponents/PrivateProfileInfo';
import PageDataContext from '../context/PageDataContext';
import { AuthenticatedComponent } from '../auth/keycloak';
import { MobileViewPage } from '../component/globalComponents/MobileViewPage';
import { BackIcon } from '../component/iconComponents/BackIcon';
import { useNavigate } from 'react-router-dom';
import { ProfileFollowers } from './ProfileFollowers';
import { FormattedMessage, useIntl } from 'react-intl';
import { getQuantityStringByIntl } from '../util/label';
import { getUserSubscription, SubscriptionDef } from '../service/inventory';
import { ProfileSubscriptions } from './ProfileSubscriptions';

export const ProfilePage: AuthenticatedComponent = ({ keycloak }) => {
    const { isLoadFromCache } = useContext(PageDataContext);

    const intl = useIntl();
    const navigate = useNavigate();

    const { colorMode } = useColorMode();

    const DEFAULT_UI_STATE = 'default';
    const FOLLOW_UI_STATE = 'follow';
    const SUBSCRIBE_UI_STATE = 'subscribe';
    const [uiState, setUiState] = useState<string>(DEFAULT_UI_STATE);

    const [userProfile, setUserProfile] = usePageDataCache<UserProfile | undefined>(
        'userProfile',
        undefined,
    );

    const [followersCount, setFollowersCount] = usePageDataCache(
        `PrivateFollowStats_followersCount`,
        0,
    );
    const [followingCount, setFollowingCount] = usePageDataCache(
        `PrivateFollowStats_followingCount`,
        0,
    );
    const [subscriptions, setSubscriptions] = usePageDataCache<SubscriptionDef[]>(
        `PrivateFollowStats_subscriptions`,
        [],
    );

    const [username, setUsername] = useState('username');
    const [blurb, setBlurb] = useState('blurb');
    const [isLoading, setIsLoading] = useState(true);
    const [imageUrl, setImageUrl] = usePageDataCache(
        'imageUrl',
        userProfile?.avatar_object_url || '',
    );

    const updateUiState = (state: string) => {
        switch (uiState) {
            case DEFAULT_UI_STATE:
                setUiState(state);
                break;
            case FOLLOW_UI_STATE:
                state === FOLLOW_UI_STATE ? setUiState(DEFAULT_UI_STATE) : setUiState(state);
                break;
            case SUBSCRIBE_UI_STATE:
                state === SUBSCRIBE_UI_STATE ? setUiState(DEFAULT_UI_STATE) : setUiState(state);
                break;
        }
    };

    const countActiveSubscriptions = () => {
        return subscriptions.filter(
            (sub) =>
                Boolean(sub.status.inactive) !== true &&
                sub.subscription_key.subscribe_target !== 'streamsix',
        ).length;
    };

    useEffect(() => {
        async function getProfileInfo() {
            const profile = await getUserPrivateProfile(keycloak.subject, keycloak.token);
            if (!profile.display_email_publicly) {
                profile.email = '';
            }
            setUserProfile(profile);
        }
        if (!isLoadFromCache || userProfile === undefined) {
            getProfileInfo();
        }
    }, [keycloak]);

    useEffect(() => {
        if (userProfile) {
            setUsername(userProfile.username || 'User');
            setBlurb(userProfile.description);
            setImageUrl(userProfile.avatar_object_url || config.avatarPlaceholderURL);
            setIsLoading(false);
        }
    }, [userProfile]);

    useEffect(() => {
        async function loadFollowStats() {
            const userID = keycloak.subject;
            const followingCount = await getUserFollowingCount(userID, keycloak.token);
            const followersCount = await getUserFollowersCount(userID);
            setFollowingCount(followingCount);
            setFollowersCount(followersCount);
        }

        async function loadSubscriptions() {
            const subList = await getUserSubscription(keycloak.subject, '', '', keycloak.token);
            setSubscriptions(subList);
        }

        if (!isLoadFromCache) {
            loadFollowStats();
            loadSubscriptions();
        }
    }, []);

    return (
        <>
            {isLoading && userProfile ? (
                <Center width="100%" height="100%">
                    <Spinner data-cy="loading" />
                </Center>
            ) : (
                <>
                    <BrowserView>
                        <BrowserTopBar
                            color="#FFF"
                            isLoading={isLoading}
                            loadingColor={'#6ef1d5'}
                        ></BrowserTopBar>
                        <BrowserSidebar />
                        <Box className="profile-browser-content">
                            <Flex className="profile-expand-container">
                                <Flex>
                                    <Image
                                        className="profile-banner-browser"
                                        src={colorMode === 'dark' ? DarkHeader : LightHeader}
                                        fallback={<Skeleton height="100%" width="100%" />}
                                    />
                                </Flex>
                                <Flex className={'profile-browser-flex'}>
                                    <PrivateProfileInfo
                                        username={username}
                                        imageUrl={imageUrl}
                                        blurb={blurb}
                                        keycloak={keycloak}
                                    />
                                    <Flex className="profile-buttons-browser">
                                        <Flex className={'private-follow-stats'}>
                                            <Flex className={'button-list-browser'}>
                                                <Button
                                                    className={`following-button ${
                                                        uiState === FOLLOW_UI_STATE && 'selected'
                                                    }`}
                                                    onClick={() => updateUiState(FOLLOW_UI_STATE)}
                                                >
                                                    <FormattedMessage
                                                        id="private_profile.following_count"
                                                        defaultMessage={'Following {count}'}
                                                        values={{
                                                            count: getQuantityStringByIntl(
                                                                followingCount,
                                                                intl,
                                                            ),
                                                        }}
                                                    />
                                                </Button>
                                                <Button
                                                    className={`subscriptions-button ${
                                                        uiState === SUBSCRIBE_UI_STATE && 'selected'
                                                    }`}
                                                    onClick={() =>
                                                        updateUiState(SUBSCRIBE_UI_STATE)
                                                    }
                                                >
                                                    <FormattedMessage
                                                        id="private_profile.subscriptions_count"
                                                        defaultMessage={'Subscriptions {count}'}
                                                        values={{
                                                            count: countActiveSubscriptions(),
                                                        }}
                                                    />
                                                </Button>
                                            </Flex>
                                        </Flex>
                                    </Flex>
                                </Flex>
                                <Flex className={'profile-divider'}>
                                    <Flex className="divider" />
                                </Flex>
                                {uiState === DEFAULT_UI_STATE ? (
                                    <ProfileTabs userProfile={userProfile} />
                                ) : uiState === FOLLOW_UI_STATE ? (
                                    <ProfileFollowers keycloak={keycloak} />
                                ) : (
                                    <ProfileSubscriptions keycloak={keycloak} />
                                )}
                            </Flex>
                        </Box>
                    </BrowserView>
                    <MobileViewPage
                        title=""
                        titleColor=""
                        titleBackgroundColor="#6ef1d5"
                        customBanner={
                            <Flex className="profile-back-button" onClick={() => navigate(-1)}>
                                <BackIcon />
                            </Flex>
                        }
                        isLoading={isLoading}
                    >
                        <Flex className="profile-expand-container">
                            <Flex>
                                <Image
                                    className="profile-banner"
                                    src={colorMode === 'dark' ? DarkHeader : LightHeader}
                                    fallback={<Skeleton height="100%" width="100%" />}
                                />
                            </Flex>
                            <Flex className={'private-profile-info'}>
                                <PrivateProfileInfo
                                    username={username}
                                    imageUrl={imageUrl}
                                    blurb={blurb}
                                    keycloak={keycloak}
                                />

                                <Flex className={'divider'} />

                                <Flex className="profile-follower">
                                    <PrivateFollowStats keycloak={keycloak} />
                                </Flex>

                                <Flex className={'divider'} />
                            </Flex>
                            <ProfileTabs userProfile={userProfile} />
                        </Flex>
                    </MobileViewPage>
                </>
            )}
        </>
    );
};
